import React, { useEffect, useState } from "react";
import {
  Container,
  Text,
  Button,
  Modal,
  TextInput,
  Textarea,
  Select,
  Stack,
  Group,
  MultiSelect,
  NumberInput,
  Paper,
  Title,
  Image,
  Table,
  ActionIcon,
  Badge,
  Alert,
  Loader,
  Tabs,
} from "@mantine/core";
import { useDisclosure } from "@mantine/hooks";
import { notifications } from "@mantine/notifications";
import { IconEdit, IconTrash } from "@tabler/icons-react";
import axios from "axios";

const API_URL = import.meta.env.VITE_API_BASE_URL || "https://thalvora-exports.onrender.com";

type SelectOption = { value: string; label: string };

interface Product {
  id: string;
  name: string;
  slug: string;
  category: { id: string; name: string };
  images: string[];
  description: string;
  shortDescription: string;
  origin: string;
  packaging: string[];
  shelfLife: string;
  moq: string;
  exportAvailability: string[];
  qualityInfo: string;
  specifications: Record<string, string>;
  orderIndex: number;
  active: boolean;
  createdAt: string;
}

interface Category {
  id: string;
  name: string;
  slug: string;
  description: string;
  imageUrl: string;
  displayOrder: number;
  active: boolean;
  productCount: number;
}

export const Products: React.FC = () => {
  // Product states
  const [productModalOpened, { open: openProductModal, close: closeProductModal }] = useDisclosure(false);
  const [editProductModalOpened, { open: openEditProduct, close: closeEditProduct }] = useDisclosure(false);
  const [deleteProductModalOpened, { open: openDeleteProduct, close: closeDeleteProduct }] = useDisclosure(false);
  
  // Category states
  const [categoryModalOpened, { open: openCategoryModal, close: closeCategoryModal }] = useDisclosure(false);
  const [editCategoryModalOpened, { open: openEditCategory, close: closeEditCategory }] = useDisclosure(false);
  const [deleteCategoryModalOpened, { open: openDeleteCategory, close: closeDeleteCategory }] = useDisclosure(false);

  const [categories, setCategories] = useState<SelectOption[]>([]);
  const [allCategories, setAllCategories] = useState<Category[]>([]);
  const [products, setProducts] = useState<Product[]>([]);
  const [loading, setLoading] = useState(false);
  const [fetchLoading, setFetchLoading] = useState(true);
  const [selectedProduct, setSelectedProduct] = useState<Product | null>(null);
  const [selectedCategory, setSelectedCategory] = useState<Category | null>(null);
  const [imagePreviews, setImagePreviews] = useState<string[]>([]);
  const [imageFiles, setImageFiles] = useState<File[]>([]);

  // Category image states
  const [categoryImageFile, setCategoryImageFile] = useState<File | null>(null);
  const [categoryImagePreview, setCategoryImagePreview] = useState<string>("");

  // Product form
  const [productForm, setProductForm] = useState<any>({
    name: "",
    categoryId: "",
    images: [],
    description: "",
    shortDescription: "",
    origin: "",
    packaging: [],
    shelfLife: "",
    moq: "",
    exportAvailability: [],
    qualityInfo: "",
    specifications: {},
    orderIndex: 1,
    active: true,
  });

  // Category form
  const [categoryForm, setCategoryForm] = useState({
    name: "",
    description: "",
    imageUrl: "",
    displayOrder: 0,
    active: true,
  });

  // Fetch data
  useEffect(() => {
    fetchCategories();
    fetchProducts();
    fetchAllCategories();
  }, []);

  const fetchCategories = async () => {
    try {
      const res = await axios.get(`${API_URL}/categories/public/with-count`);
      const formatted: SelectOption[] = res.data.map((c: any) => ({
        value: String(c.id),
        label: c.name,
      }));
      setCategories(formatted);
    } catch (err) {
      console.error("Category error:", err);
    }
  };

  const fetchAllCategories = async () => {
    try {
      const res = await axios.get(`${API_URL}/categories/public/with-count`);
      setAllCategories(res.data);
    } catch (err) {
      console.error("Error fetching categories:", err);
    }
  };

  const fetchProducts = async () => {
    setFetchLoading(true);
    try {
      const res = await axios.get(`${API_URL}/products/public`);
      setProducts(res.data);
    } catch (err) {
      console.error("Error fetching products:", err);
      notifications.show({ title: "Error", message: "Failed to load products", color: "red" });
    } finally {
      setFetchLoading(false);
    }
  };

  // Product image upload
  const uploadProductImage = async (file: File, productId?: string) => {
    const formData = new FormData();
    const metadata = productId ? { productId } : {};
    formData.append(
      "data",
      new Blob(
        [
          JSON.stringify({
            title: file.name,
            type: "IMAGE",
            active: true,
            metadata: JSON.stringify(metadata),
          }),
        ],
        { type: "application/json" }
      )
    );
    formData.append("file", file);
    const token = localStorage.getItem("token");
    const res = await axios.post(`${API_URL}/gallery/PRODUCTS/items`, formData, {
      headers: { Authorization: `Bearer ${token}` },
    });
    const lastItem = res.data.items[res.data.items.length - 1];
    return lastItem.imageUrl;
  };

  const handleProductFileUpload = async (files: FileList | null, productId?: string) => {
    if (!files) return;
    const previews: string[] = [];
    const uploadedUrls: string[] = [];
    for (const file of Array.from(files)) {
      const preview = URL.createObjectURL(file);
      previews.push(preview);
      try {
        const url = await uploadProductImage(file, productId);
        uploadedUrls.push(url);
        notifications.show({ title: "Uploaded", message: `${file.name} uploaded successfully`, color: "green" });
      } catch (err) {
        notifications.show({ title: "Error", message: `Failed to upload ${file.name}`, color: "red" });
      }
    }
    setImagePreviews((prev) => [...prev, ...previews]);
    setProductForm((prev: any) => ({
      ...prev,
      images: [...prev.images, ...uploadedUrls],
    }));
  };

  const handleProductImageUrl = (value: string) => {
    if (!value) return;
    const urls = value.split(",").map((u) => u.trim());
    setImagePreviews((prev) => [...prev, ...urls]);
    setProductForm({ ...productForm, images: [...productForm.images, ...urls] });
  };

  // Category image upload
  const uploadCategoryImage = async (file: File) => {
    const formData = new FormData();
    formData.append("file", file);
    const token = localStorage.getItem("token");
    const res = await axios.post(`${API_URL}/categories/admin/upload`, formData, {
      headers: { Authorization: `Bearer ${token}` },
    });
    return res.data; // returns imageUrl string
  };

  const handleCategoryFileUpload = async (file: File | null) => {
    if (!file) return;
    const preview = URL.createObjectURL(file);
    setCategoryImagePreview(preview);
    setCategoryImageFile(file);
    try {
      const uploadedUrl = await uploadCategoryImage(file);
      setCategoryForm(prev => ({ ...prev, imageUrl: uploadedUrl }));
      notifications.show({ title: "Uploaded", message: "Category image uploaded successfully", color: "green" });
    } catch (err) {
      notifications.show({ title: "Error", message: "Failed to upload category image", color: "red" });
    }
  };

  // Product CRUD
  const handleCreateProduct = async () => {
    if (!productForm.categoryId) {
      notifications.show({ title: "Error", message: "Please select a category", color: "red" });
      return;
    }
    setLoading(true);
    try {
      const token = localStorage.getItem("token");
      const response = await axios.post(`${API_URL}/products/admin`, productForm, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const newProduct = response.data;
      if (imageFiles.length > 0) {
        const uploadedUrls = await Promise.all(imageFiles.map(file => uploadProductImage(file, newProduct.id)));
        await axios.put(`${API_URL}/products/admin/${newProduct.id}`, {
          ...productForm,
          images: uploadedUrls,
        }, { headers: { Authorization: `Bearer ${token}` } });
      }
      notifications.show({ title: "Success", message: "Product created", color: "green" });
      closeProductModal();
      resetProductForm();
      fetchProducts();
    } catch (err) {
      notifications.show({ title: "Error", message: "Failed to create product", color: "red" });
    } finally {
      setLoading(false);
    }
  };

  const handleEditProduct = (product: Product) => {
    setSelectedProduct(product);
    setProductForm({
      name: product.name,
      categoryId: product.category.id,
      images: product.images,
      description: product.description || "",
      shortDescription: product.shortDescription || "",
      origin: product.origin || "",
      packaging: product.packaging || [],
      shelfLife: product.shelfLife || "",
      moq: product.moq || "",
      exportAvailability: product.exportAvailability || [],
      qualityInfo: product.qualityInfo || "",
      specifications: product.specifications || {},
      orderIndex: product.orderIndex || 1,
      active: product.active,
    });
    setImagePreviews(product.images);
    setImageFiles([]);
    openEditProduct();
  };

  const handleUpdateProduct = async () => {
    if (!selectedProduct) return;
    setLoading(true);
    try {
      const token = localStorage.getItem("token");
      let allImages = [...productForm.images];
      if (imageFiles.length > 0) {
        const newUrls = await Promise.all(imageFiles.map(file => uploadProductImage(file, selectedProduct.id)));
        allImages = [...allImages, ...newUrls];
      }
      const payload = { ...productForm, images: allImages };
      await axios.put(`${API_URL}/products/admin/${selectedProduct.id}`, payload, {
        headers: { Authorization: `Bearer ${token}` },
      });
      notifications.show({ title: "Success", message: "Product updated", color: "green" });
      closeEditProduct();
      resetProductForm();
      fetchProducts();
    } catch (err) {
      notifications.show({ title: "Error", message: "Failed to update product", color: "red" });
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteProduct = async () => {
    if (!selectedProduct) return;
    setLoading(true);
    try {
      const token = localStorage.getItem("token");
      await axios.delete(`${API_URL}/products/admin/${selectedProduct.id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      notifications.show({ title: "Success", message: "Product deleted", color: "green" });
      closeDeleteProduct();
      fetchProducts();
    } catch (err) {
      notifications.show({ title: "Error", message: "Failed to delete product", color: "red" });
    } finally {
      setLoading(false);
    }
  };

  const resetProductForm = () => {
    setProductForm({
      name: "",
      categoryId: "",
      images: [],
      description: "",
      shortDescription: "",
      origin: "",
      packaging: [],
      shelfLife: "",
      moq: "",
      exportAvailability: [],
      qualityInfo: "",
      specifications: {},
      orderIndex: 1,
      active: true,
    });
    setImagePreviews([]);
    setImageFiles([]);
    setSelectedProduct(null);
  };

  // Category CRUD
  const handleCreateCategory = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem("token");
      await axios.post(`${API_URL}/categories/admin`, categoryForm, {
        headers: { Authorization: `Bearer ${token}` },
      });
      notifications.show({ title: "Success", message: "Category created", color: "green" });
      closeCategoryModal();
      resetCategoryForm();
      fetchCategories();
      fetchAllCategories();
    } catch (err) {
      notifications.show({ title: "Error", message: "Failed to create category", color: "red" });
    } finally {
      setLoading(false);
    }
  };

  const handleEditCategory = (category: Category) => {
    setSelectedCategory(category);
    setCategoryForm({
      name: category.name,
      description: category.description || "",
      imageUrl: category.imageUrl || "",
      displayOrder: category.displayOrder || 0,
      active: category.active,
    });
    setCategoryImagePreview(category.imageUrl || "");
    setCategoryImageFile(null);
    openEditCategory();
  };

  const handleUpdateCategory = async () => {
    if (!selectedCategory) return;
    setLoading(true);
    try {
      const token = localStorage.getItem("token");
      await axios.put(`${API_URL}/categories/admin/${selectedCategory.id}`, categoryForm, {
        headers: { Authorization: `Bearer ${token}` },
      });
      notifications.show({ title: "Success", message: "Category updated", color: "green" });
      closeEditCategory();
      resetCategoryForm();
      fetchCategories();
      fetchAllCategories();
    } catch (err) {
      notifications.show({ title: "Error", message: "Failed to update category", color: "red" });
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteCategory = async () => {
    if (!selectedCategory) return;
    setLoading(true);
    try {
      const token = localStorage.getItem("token");
      await axios.delete(`${API_URL}/categories/admin/${selectedCategory.id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      notifications.show({ title: "Success", message: "Category deleted", color: "green" });
      closeDeleteCategory();
      fetchCategories();
      fetchAllCategories();
      fetchProducts(); // products may lose category, refresh
    } catch (err) {
      notifications.show({ title: "Error", message: "Failed to delete category", color: "red" });
    } finally {
      setLoading(false);
    }
  };

  const resetCategoryForm = () => {
    setCategoryForm({
      name: "",
      description: "",
      imageUrl: "",
      displayOrder: 0,
      active: true,
    });
    setCategoryImagePreview("");
    setCategoryImageFile(null);
    setSelectedCategory(null);
  };

  const addSpecification = () => {
    setProductForm({
      ...productForm,
      specifications: { ...productForm.specifications, "New Key": "Value" },
    });
  };

  const updateSpec = (key: string, value: string) => {
    setProductForm({
      ...productForm,
      specifications: { ...productForm.specifications, [key]: value },
    });
  };

  const renderProductForm = (onSubmit: () => void, submitLabel: string) => (
    <Stack>
      <TextInput
        label="Product Name"
        value={productForm.name}
        onChange={(e) => setProductForm({ ...productForm, name: e.target.value })}
        required
      />
      <Select
        label="Category"
        data={categories}
        value={productForm.categoryId || null}
        onChange={(val) => setProductForm({ ...productForm, categoryId: val ? String(val) : "" })}
        placeholder="Select category"
        searchable
        required
      />
      <TextInput
        label="Add Image URL"
        placeholder="Paste URLs separated by comma"
        onBlur={(e) => handleProductImageUrl(e.target.value)}
      />
      <input
        type="file"
        multiple
        accept="image/*"
        onChange={(e) => {
          if (e.target.files) {
            setImageFiles(Array.from(e.target.files));
            handleProductFileUpload(e.target.files);
          }
        }}
      />
      <Group>
        {imagePreviews.map((img, i) => (
          <Image key={i} src={img} width={80} height={80} fit="cover" radius="sm" loading="lazy"  />
        ))}
      </Group>
      <Textarea
        label="Description"
        value={productForm.description}
        onChange={(e) => setProductForm({ ...productForm, description: e.target.value })}
      />
      <Textarea
        label="Short Description"
        value={productForm.shortDescription}
        onChange={(e) => setProductForm({ ...productForm, shortDescription: e.target.value })}
      />
      <TextInput
        label="Origin"
        value={productForm.origin}
        onChange={(e) => setProductForm({ ...productForm, origin: e.target.value })}
      />
      <MultiSelect
        label="Packaging"
        data={["5kg", "10kg", "25kg", "50kg", "1MT", "2MT"]}
        value={productForm.packaging}
        onChange={(val) => setProductForm({ ...productForm, packaging: val })}
      />
      <TextInput
        label="Shelf Life"
        value={productForm.shelfLife}
        onChange={(e) => setProductForm({ ...productForm, shelfLife: e.target.value })}
      />
      <TextInput
        label="MOQ"
        value={productForm.moq}
        onChange={(e) => setProductForm({ ...productForm, moq: e.target.value })}
      />
      <MultiSelect
        label="Export Availability"
        data={["Worldwide", "India", "UAE", "USA", "UK", "Canada", "Australia"]}
        value={productForm.exportAvailability}
        onChange={(val) => setProductForm({ ...productForm, exportAvailability: val })}
      />
      <Textarea
        label="Quality Info"
        value={productForm.qualityInfo}
        onChange={(e) => setProductForm({ ...productForm, qualityInfo: e.target.value })}
      />
      <NumberInput
        label="Order Index"
        value={productForm.orderIndex}
        onChange={(val) => setProductForm({ ...productForm, orderIndex: val || 1 })}
      />
      <Paper p="sm" withBorder>
        <Group justify="space-between">
          <Text fw={600}>Specifications</Text>
          <Button size="xs" onClick={addSpecification}>+ Add</Button>
        </Group>
        {Object.entries(productForm.specifications).map(([key, value]: any, i) => (
          <Group key={i} mt="xs">
            <TextInput
              placeholder="Key"
              value={key}
              onChange={(e) => {
                const newKey = e.target.value;
                const newSpecs = { ...productForm.specifications };
                delete newSpecs[key];
                newSpecs[newKey] = value;
                setProductForm({ ...productForm, specifications: newSpecs });
              }}
            />
            <TextInput
              placeholder="Value"
              value={value}
              onChange={(e) => updateSpec(key, e.target.value)}
            />
          </Group>
        ))}
      </Paper>
      <Button loading={loading} onClick={onSubmit}>{submitLabel}</Button>
    </Stack>
  );

  const renderCategoryForm = (onSubmit: () => void, submitLabel: string) => (
    <Stack>
      <TextInput
        label="Category Name"
        value={categoryForm.name}
        onChange={(e) => setCategoryForm({ ...categoryForm, name: e.target.value })}
        required
      />
      <Textarea
        label="Description"
        value={categoryForm.description}
        onChange={(e) => setCategoryForm({ ...categoryForm, description: e.target.value })}
      />
      <TextInput
        label="Image URL"
        placeholder="https://example.com/image.jpg"
        value={categoryForm.imageUrl}
        onChange={(e) => setCategoryForm({ ...categoryForm, imageUrl: e.target.value })}
      />
      <input
        type="file"
        accept="image/*"
        onChange={(e) => {
          if (e.target.files && e.target.files[0]) {
            handleCategoryFileUpload(e.target.files[0]);
          }
        }}
      />
      {categoryImagePreview && (
        <Image src={categoryImagePreview} width={100} height={100} fit="cover" radius="sm" />
      )}
      <NumberInput
        label="Display Order"
        value={categoryForm.displayOrder}
        onChange={(val) => setCategoryForm({ ...categoryForm, displayOrder: typeof val === 'number' ? val : 0 })}
      />
      <Button loading={loading} onClick={onSubmit}>{submitLabel}</Button>
    </Stack>
  );

  return (
    <Container size="lg">
      <Tabs defaultValue="products">
        <Tabs.List>
          <Tabs.Tab value="products">Products</Tabs.Tab>
          <Tabs.Tab value="categories">Categories</Tabs.Tab>
        </Tabs.List>

        {/* Products Tab */}
        <Tabs.Panel value="products" pt="lg">
          <Group justify="space-between" mb="lg">
            <Title order={3}>Products Management</Title>
            <Button onClick={openProductModal}>+ Add Product</Button>
          </Group>

          <Paper withBorder>
            {fetchLoading ? (
              <div className="flex justify-center p-8"><Loader /></div>
            ) : products.length === 0 ? (
              <Alert color="blue">No products found. Click "Add Product" to create one.</Alert>
            ) : (
              <Table striped highlightOnHover>
                <Table.Thead>
                  <Table.Tr>
                    <Table.Th>Name</Table.Th>
                    <Table.Th>Category</Table.Th>
                    <Table.Th>Origin</Table.Th>
                    <Table.Th>MOQ</Table.Th>
                    <Table.Th>Status</Table.Th>
                    <Table.Th>Actions</Table.Th>
                  </Table.Tr>
                </Table.Thead>
                <Table.Tbody>
                  {products.map((product) => (
                    <Table.Tr key={product.id}>
                      <Table.Td>{product.name}</Table.Td>
                      <Table.Td>{product.category.name}</Table.Td>
                      <Table.Td>{product.origin || "-"}</Table.Td>
                      <Table.Td>{product.moq || "-"}</Table.Td>
                      <Table.Td>
                        <Badge color={product.active ? "green" : "red"}>{product.active ? "Active" : "Inactive"}</Badge>
                      </Table.Td>
                      <Table.Td>
                        <Group gap="xs">
                          <ActionIcon color="blue" onClick={() => handleEditProduct(product)}>
                            <IconEdit size={16} />
                          </ActionIcon>
                          <ActionIcon color="red" onClick={() => { setSelectedProduct(product); openDeleteProduct(); }}>
                            <IconTrash size={16} />
                          </ActionIcon>
                        </Group>
                      </Table.Td>
                    </Table.Tr>
                  ))}
                </Table.Tbody>
              </Table>
            )}
          </Paper>
        </Tabs.Panel>

        {/* Categories Tab */}
        <Tabs.Panel value="categories" pt="lg">
          <Group justify="space-between" mb="lg">
            <Title order={3}>Categories Management</Title>
            <Button onClick={openCategoryModal}>+ Add Category</Button>
          </Group>

          <Paper withBorder>
            {fetchLoading ? (
              <div className="flex justify-center p-8"><Loader /></div>
            ) : allCategories.length === 0 ? (
              <Alert color="blue">No categories found. Click "Add Category" to create one.</Alert>
            ) : (
              <Table striped highlightOnHover>
                <Table.Thead>
                  <Table.Tr>
                    <Table.Th>Name</Table.Th>
                    <Table.Th>Description</Table.Th>
                    <Table.Th>Products</Table.Th>
                    <Table.Th>Order</Table.Th>
                    <Table.Th>Status</Table.Th>
                    <Table.Th>Actions</Table.Th>
                  </Table.Tr>
                </Table.Thead>
                <Table.Tbody>
                  {allCategories.map((category) => (
                    <Table.Tr key={category.id}>
                      <Table.Td>{category.name}</Table.Td>
                      <Table.Td>{category.description || "-"}</Table.Td>
                      <Table.Td>{category.productCount}</Table.Td>
                      <Table.Td>{category.displayOrder}</Table.Td>
                      <Table.Td>
                        <Badge color={category.active ? "green" : "red"}>{category.active ? "Active" : "Inactive"}</Badge>
                      </Table.Td>
                      <Table.Td>
                        <Group gap="xs">
                          <ActionIcon color="blue" onClick={() => handleEditCategory(category)}>
                            <IconEdit size={16} />
                          </ActionIcon>
                          <ActionIcon color="red" onClick={() => { setSelectedCategory(category); openDeleteCategory(); }}>
                            <IconTrash size={16} />
                          </ActionIcon>
                        </Group>
                      </Table.Td>
                    </Table.Tr>
                  ))}
                </Table.Tbody>
              </Table>
            )}
          </Paper>
        </Tabs.Panel>
      </Tabs>

      {/* Product Modals */}
      <Modal opened={productModalOpened} onClose={closeProductModal} title="Create Product" size="lg">
        {renderProductForm(handleCreateProduct, "Create Product")}
      </Modal>
      <Modal opened={editProductModalOpened} onClose={closeEditProduct} title="Edit Product" size="lg">
        {renderProductForm(handleUpdateProduct, "Update Product")}
      </Modal>
      <Modal opened={deleteProductModalOpened} onClose={closeDeleteProduct} title="Confirm Deletion">
        <Text>Delete <strong>{selectedProduct?.name}</strong>? This action cannot be undone.</Text>
        <Group justify="flex-end" mt="md">
          <Button variant="light" onClick={closeDeleteProduct}>Cancel</Button>
          <Button color="red" onClick={handleDeleteProduct} loading={loading}>Delete</Button>
        </Group>
      </Modal>

      {/* Category Modals */}
      <Modal opened={categoryModalOpened} onClose={closeCategoryModal} title="Create Category" size="md">
        {renderCategoryForm(handleCreateCategory, "Create Category")}
      </Modal>
      <Modal opened={editCategoryModalOpened} onClose={closeEditCategory} title="Edit Category" size="md">
        {renderCategoryForm(handleUpdateCategory, "Update Category")}
      </Modal>
      <Modal opened={deleteCategoryModalOpened} onClose={closeDeleteCategory} title="Confirm Deletion">
        <Text>Delete category <strong>{selectedCategory?.name}</strong>? Products in this category will lose association.</Text>
        <Group justify="flex-end" mt="md">
          <Button variant="light" onClick={closeDeleteCategory}>Cancel</Button>
          <Button color="red" onClick={handleDeleteCategory} loading={loading}>Delete</Button>
        </Group>
      </Modal>
    </Container>
  );
};